// PowerComposer — the data-driven vocabulary the host app supplies.
//
// Every varying shape here is a named type with a discriminant where one
// exists (kind), never a raw object literal. Nothing in this file is a
// fixture — the sketch's fixtures (composerFixtures.ts) do NOT ship into
// production; the host app is the one and only source of candidates,
// commands, and audience.

// The three resource kinds a chat turn can reference (mirrors the sketch
// vocabulary in sketches/chat/sketchTypes.ts's `SketchRefKind` — kept as a
// closed union here, independent of that exploratory file, since this is
// now the production surface). `package` borrows the folder glyph in
// ResourceMentionChip (no dedicated icon yet) — a flagged stand-in, not an
// oversight.
export type ComposerResourceKind = "source" | "material" | "package" | "file";

// The optimistic ingest lifecycle a chip can be decorated with (F83).
// VIEW-TRANSIENT, never a durable field — the shell folds it at render from
// the ingest batch, and it is reconciled away the moment the real
// `file_landed` event arrives. `undefined` ⇒ today's static
// chip (backward-compatible): a resting reference with no lifecycle to show.
//   uploading — the drop's bytes are in flight (the ingest `pending` state)
//   ready     — landed, its text readable; the calm resting chip (the
//               background understanding sweep is not a usability gate, D57)
//   failed    — the upload round-trip failed (a basic failed chip; retry is a
//               later slice, so no live control rides here yet)
//   reading     — landed, but scanned pages are still being read (OCR); the
//                 source's durable read status (FR-3.3/3.4)
//   read_failed — landed, but reading its scanned pages failed (FR-3.6)
export type ComposerAttachmentStatus =
    | "uploading"
    | "ready"
    | "failed"
    | "reading"
    | "read_failed";

// One mentionable resource — used BOTH as a candidate the @ menu offers and
// as the chip riding in the draft/context row once picked. Same shape, two
// roles (mirrors the sketch's `SketchResourceRef`): a picked mention IS one
// of the offered candidates, never a freeform typed reference.
export type ComposerResourceRef = {
    readonly kind: ComposerResourceKind;
    readonly id: string;
    readonly title: string;
    // A material the agent authored (FR-2.7): shown with the AI mark.
    readonly aiAuthored?: true;
    // The optimistic lifecycle this chip is in, when it is a live upload/
    // enrichment (F83). Absent ⇒ a resting reference (today's static chip).
    readonly status?: ComposerAttachmentStatus;
};

// A candidate narrowed to a SOURCE — the only kind the "+" attach picker
// offers (D3: chat attachment is sources-only; a material stays reachable
// as an `@`-reference, never as an attachment).
export type ComposerSourceRef = ComposerResourceRef & {
    readonly kind: "source";
};

export const isComposerSourceRef = (
    ref: ComposerResourceRef,
): ref is ComposerSourceRef => ref.kind === "source";

// One slash action: a label + description for the menu row, and the literal
// text it inserts into the draft on pick — no hidden magic beyond "fill the
// draft with this text".
export type ComposerSlashCommand = {
    readonly id: string;
    readonly label: string;
    readonly description: string;
    readonly insertText: string;
};

// The conversation-level grounding the context row surfaces (today's
// SituationProfileChip, lifted into the composer's own frame per the
// research doc §4/§7.2 "scope is shown before it's spent"). Title-only: the
// full facet register is that component's own read-back, not duplicated
// here.
export type ComposerAudience = {
    readonly title: string;
};
